import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_INFO = {
  "20": { color: "green", label: "20%", duration: "3 meses", people: "100", credits: "12,000" },
  "30": { color: "yellow", label: "30%", duration: "4 meses", people: "300", credits: "10,500" },
  "50": { color: "red", label: "50%", duration: "6 meses", people: "500", credits: "7,500" },
};

const AdminDiscountCodes = () => {
  const [codes, setCodes] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ tier: '20', code: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      setLoading(true);
      const [codesRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/discount-codes`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/discount-codes/stats`, { headers })
      ]);
      setCodes(codesRes.data.codes || []);
      setStats(statsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/discount-codes`, form, { headers });
      setForm({ tier: '20', code: '', notes: '' });
      setShowForm(false);
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al crear codigo');
    } finally {
      setSaving(false);
    }
  };

  const toggleCode = async (id) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/discount-codes/${id}/toggle`, {}, { headers });
      load();
    } catch (err) {
      alert('Error al actualizar');
    }
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Eliminar este codigo de descuento?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/discount-codes/${id}`, { headers });
      load();
    } catch (err) {
      alert('Error al eliminar');
    }
  };

  return (
    <div className="font-display bg-background-light dark:bg-background-dark min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Header />
        <div className="py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl font-black text-gray-900 dark:text-white" data-testid="admin-discounts-title">
                Codigos de Descuento
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">Sistema de invitaciones con descuento para Plan Estandar</p>
            </div>
            <button onClick={() => setShowForm(!showForm)} data-testid="create-discount-btn"
              className="px-4 py-2 bg-primary text-white rounded-lg font-semibold hover:bg-primary/90 transition-colors">
              {showForm ? 'Cancelar' : '+ Nuevo Codigo'}
            </button>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-4 shadow-lg text-center">
                <p className="text-2xl font-bold text-blue-500">{stats.total_codes}</p>
                <p className="text-xs text-gray-500 mt-1">Codigos creados</p>
              </div>
              <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-4 shadow-lg text-center">
                <p className="text-2xl font-bold text-green-500">{stats.active_codes}</p>
                <p className="text-xs text-gray-500 mt-1">Codigos activos</p>
              </div>
              <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-4 shadow-lg text-center">
                <p className="text-2xl font-bold text-purple-500">{stats.total_redemptions}</p>
                <p className="text-xs text-gray-500 mt-1">Canjes totales</p>
              </div>
              <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-4 shadow-lg text-center">
                <p className="text-2xl font-bold text-orange-500">{stats.active_user_discounts}</p>
                <p className="text-xs text-gray-500 mt-1">Descuentos activos</p>
              </div>
            </div>
          )}

          {/* Tiers Reference */}
          <div className="bg-white dark:bg-[#1a2530] rounded-2xl p-6 shadow-lg mb-8">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Niveles de Descuento</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(TIER_INFO).map(([tier, info]) => (
                <div key={tier} className={`border-l-4 border-${info.color}-500 bg-gray-50 dark:bg-[#283039] rounded-lg p-4`}>
                  <p className="font-bold text-lg text-gray-900 dark:text-white">{info.label} Descuento</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Duracion: {info.duration}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Max personas: {info.people}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Creditos: {info.credits}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Create Form */}
          {showForm && (
            <form onSubmit={handleCreate} className="bg-white dark:bg-[#1a2530] rounded-2xl p-6 shadow-lg mb-8" data-testid="discount-form">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Crear Codigo de Descuento</h3>
              <div className="grid md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nivel de Descuento *</label>
                  <select value={form.tier} onChange={e => setForm({...form, tier: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                    data-testid="discount-tier-select">
                    <option value="20">20% — Hasta 100 personas — 3 meses</option>
                    <option value="30">30% — Hasta 300 personas — 4 meses</option>
                    <option value="50">50% — Hasta 500 personas — 6 meses</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Codigo personalizado (opcional)</label>
                  <input type="text" value={form.code} onChange={e => setForm({...form, code: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                    placeholder="Ej: LANZAMIENTO20" data-testid="discount-code-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notas internas</label>
                  <input type="text" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#283039] text-gray-900 dark:text-white"
                    placeholder="Ej: Campaña influencers Mayo" data-testid="discount-notes-input" />
                </div>
              </div>
              <button type="submit" disabled={saving} data-testid="submit-discount-btn"
                className="px-6 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:opacity-50">
                {saving ? 'Creando...' : 'Crear Codigo'}
              </button>
            </form>
          )}

          {/* Codes List */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : codes.length === 0 ? (
            <div className="text-center py-16 bg-white dark:bg-[#1a2530] rounded-2xl">
              <p className="text-4xl mb-3">🎟️</p>
              <p className="text-gray-500 dark:text-gray-400">No hay codigos de descuento. Crea el primero.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {codes.map(c => {
                const tierInfo = TIER_INFO[c.tier] || {};
                const progress = Math.min((c.people_used / c.max_people) * 100, 100);
                return (
                  <div key={c.id} className={`bg-white dark:bg-[#1a2530] rounded-2xl p-5 shadow-lg border-l-4 ${c.is_active ? 'border-green-500' : 'border-gray-400 opacity-60'}`}
                    data-testid={`discount-code-${c.id}`}>
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono text-lg font-bold text-primary">{c.code}</span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-bold bg-${tierInfo.color}-100 text-${tierInfo.color}-700 dark:bg-${tierInfo.color}-900/30 dark:text-${tierInfo.color}-400`}>
                            {c.discount_percent}% OFF
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                            {c.is_active ? 'Activo' : 'Inactivo'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          ${c.discounted_price}/mes | {c.credits?.toLocaleString()} creditos | {c.duration_months} meses
                        </p>
                        {c.notes && <p className="text-xs text-gray-500 mt-1">Nota: {c.notes}</p>}
                      </div>
                      
                      {/* Counter */}
                      <div className="flex-shrink-0 w-48">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{c.people_used} personas</span>
                          <span>max {c.max_people}</span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5">
                          <div className={`h-2.5 rounded-full ${progress >= 90 ? 'bg-red-500' : progress >= 50 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{width: `${progress}%`}}></div>
                        </div>
                        <p className="text-xs text-center text-gray-500 mt-1">{c.max_people - c.people_used} disponibles</p>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 flex-shrink-0">
                        <button onClick={() => { navigator.clipboard.writeText(c.code); alert('Codigo copiado!'); }}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 hover:bg-blue-200">
                          Copiar
                        </button>
                        <button onClick={() => toggleCode(c.id)}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${c.is_active ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30' : 'bg-green-100 text-green-700 dark:bg-green-900/30'}`}>
                          {c.is_active ? 'Desactivar' : 'Activar'}
                        </button>
                        <button onClick={() => deleteCode(c.id)}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 hover:bg-red-200">
                          Eliminar
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default AdminDiscountCodes;
